'use client'

import { Stack, Box, Typography } from '@mui/material'
import { AccordionGroup } from '@biolumina/ui/components/accordionGroup/accordionGroup'
import { Button } from '@biolumina/ui/components/button/button'
import { Link } from '@biolumina/ui/components/link/link'
import { Callout } from '@biolumina/ui/components/callout/callout'
import { List } from '@biolumina/ui/components/list/list'
import { ListItem } from '@biolumina/ui/components/listItem/listItem'
import { StyledDosing } from './page.styles'
import { DosingInformationSection } from './DosingInformationSection'
import { GrapefruitInteractionSection } from './GrapefruitInteractionSection'
import { Swoosh } from '@biolumina/ui/components/swoosh/swoosh'
import { PageDivider } from '@biolumina/ui/components/pageDivider/pageDivider'
import { ImageBreakerCallout } from '@biolumina/ui/components/imageBreakerCallout/imageBreakerCallout'
import { usePageData } from '@biolumina/ui/hooks/pageData.hook'
import { Anchor } from '@biolumina/ui/components/anchor/anchor'
import { SummarySection } from '@biolumina/ui/components/summarySection/summarySection'
import Break from '@biolumina/ui/components/break/break'

export default function Dosing() {
  const { anchors } = usePageData()

  return (
    <StyledDosing>
      <Anchor id={anchors[0]}>
        <Typography variant="h2" mb={2}>
          Getting started
        </Typography>
        <Typography>
          Before you were prescribed IBTROZI, your healthcare provider likely
          performed baseline health assessments.{' '}
          <strong>
            Your healthcare provider will continue to monitor you while
            you&rsquo;re taking IBTROZI.
          </strong>
        </Typography>
        <Typography variant="body1">
          <strong>
            Before taking IBTROZI tell your healthcare provider about all your
            medical conditions, including if you:
          </strong>
        </Typography>
        <List>
          <ListItem>have liver problems.</ListItem>
          <ListItem>
            have lung or breathing problems other than lung cancer.
          </ListItem>
          <ListItem>
            have any heart problems, including a condition called long QT
            syndrome.
          </ListItem>
          <ListItem>have gout.</ListItem>
          <ListItem>are pregnant or plan to become pregnant.</ListItem>
          <ListItem>
            are breastfeeding or plan to breastfeed. It is not known if IBTROZI
            passes into your breast milk. Do not breastfeed during treatment and
            for 3 weeks after the last dose of IBTROZI.
          </ListItem>
        </List>
        <ImageBreakerCallout my={6}>
          <Typography
            mb={0}
            maxWidth={600}
            lineHeight={{ xs: 1, md: 1.5 }}
            color="#062059"
          >
            Remember, talking with your healthcare provider is always a good{' '}
            <strong>opportunity to connect</strong> and ask any questions you
            may have
          </Typography>
        </ImageBreakerCallout>
        <Typography variant="body1">
          <strong>
            Tell your healthcare provider about all the medicines you take,
          </strong>{' '}
          including prescription and over-the-counter medicines, vitamins, or
          herbal supplements.
        </Typography>
        <Typography>
          IBTROZI may affect the way other medicines work, and other medicines
          may affect how IBTROZI works.
        </Typography>
        <List>
          <ListItem>
            You should not start or stop any medicine before you talk with your
            healthcare provider who prescribed IBTROZI.
          </ListItem>
          <ListItem>
            Avoid taking proton pump inhibitor (PPI) or H2 blocker medicine. If
            you take an antacid, take it at least 2 hours before or 2 hours
            after taking IBTROZI.
          </ListItem>
        </List>
        <Typography>
          Remember, some medications interact with IBTROZI. It is important you
          let your healthcare provider know about all the medicines you&rsquo;re
          taking prior to starting IBTROZI. You should also never start a new
          medication without speaking with your healthcare provider.
        </Typography>
        <AccordionGroup
          expandAll={false}
          className="wrapped"
          items={[
            {
              id: '1',
              header:
                'Are you expecting, planning to conceive, or have a partner who is or plans to become pregnant?',
              body: (
                <Box>
                  <List>
                    <ListItem>
                      <strong>IBTROZI can harm your unborn baby.</strong>{' '}
                      IBTROZI should not be used in pregnancy. Tell your
                      healthcare provider right away if you become pregnant or
                      think you may be pregnant during treatment with IBTROZI.
                      <List sx={{ mt: 1 }}>
                        <ListItem>
                          <strong>
                            If you are a female who is able to become pregnant,
                          </strong>{' '}
                          your healthcare provider should do a pregnancy test
                          before starting IBTROZI. Use an effective birth
                          control (contraception) during treatment and for 3
                          weeks after the last dose of IBTROZI.
                        </ListItem>
                        <ListItem>
                          <strong>
                            If you are a male with a female partner who is able
                            to become pregnant,
                          </strong>{' '}
                          use an effective form of contraception during
                          treatment and for 3 weeks after the last dose of
                          IBTROZI.
                        </ListItem>
                      </List>
                    </ListItem>
                  </List>
                </Box>
              ),
            },
          ]}
        ></AccordionGroup>
      </Anchor>
      <PageDivider />
      <Anchor id={anchors[1]}>
        <Typography variant="h2" mb={2}>
          How to take IBTROZI
        </Typography>
        <Typography>
          IBTROZI comes in 200-mg capsules, and the recommended dose is 600 mg
          (3 capsules). Take IBTROZI exactly as your healthcare provider tells
          you to take it.{' '}
          <strong>
            Do not change your dose or stop taking IBTROZI unless instructed to
            do so.
          </strong>{' '}
          Your healthcare provider can change, temporarily stop, or completely
          stop your treatment with IBTROZI if you have certain side effects.
        </Typography>
        <Typography variant="h2" mb={2}>
          IBTROZI once-daily instructions
        </Typography>
        <DosingInformationSection />
        <Typography variant="h2" mb={2}>
          What if I miss a dose?
        </Typography>
        <Typography>
          <strong>If you miss a dose of IBTROZI,</strong> do not take an extra
          dose. Just skip the dose and take your next dose at the regularly
          scheduled time the next day.
        </Typography>
        <Typography>
          <strong>
            If you vomit at any time after taking a dose of IBTROZI,
          </strong>{' '}
          do not take an extra dose. Just take your next dose at the regularly
          scheduled time the next day.
        </Typography>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems="center"
            sx={{
              width: { xs: '100%', md: '210px' },
              minWidth: '206px',
              minHeight: '80px',
              overflow: 'hidden',
            }}
          >
            <img
              src="/images/taking-ibtrozi/actual-pill-icon.svg"
              alt=""
              style={{
                width: '33px',
                height: '80px',
                objectFit: 'fill',
              }}
            />
            <Typography
              sx={{
                textAlign: { xs: 'center', md: 'left' },
                color: 'primary.main',
                fontSize: '16px',
                lineHeight: '22px',
              }}
            >
              approximate
              <br />
              dimensions ~2.2 cm
            </Typography>
          </Stack>
          <Typography>
            Store IBTROZI at room temperature, between 68°F to 77°F (20°C to
            25°C).
            <br />
            <strong>
              Keep IBTROZI and all medicines out of the reach of children.
            </strong>
          </Typography>
        </Stack>{' '}
        <Typography variant="h2" mb={2}>
          Other than certain medications, what should I avoid while taking
          IBTROZI?
        </Typography>
        <GrapefruitInteractionSection />
        <Swoosh>
          <Typography
            component="strong"
            sx={{ mt: 0, mb: 0, color: 'primary.main', fontWeight: 'bold' }}
          >
            Always take IBTROZI exactly as directed by your healthcare provider
          </Typography>
        </Swoosh>
      </Anchor>
      <Anchor id={anchors[2]}>
        <SummarySection
          sx={{ my: 8 }}
          label={
            <>
              Summary:<Break md={false} />Taking IBTROZI
            </>
          }
        >
          IBTROZI<sup>&reg;</sup> (taletrectinib) comes in 200 mg capsules. The recommended starting dose is three capsules, or 600 mg, once a day. IBTROZI should be taken at the same time each day on an empty stomach, two hours before or after eating. If you miss a dose or vomit, do not take an extra dose. Take your next dose at your usual time the next day. IBTROZI should be stored at room temperature. While taking IBTROZI, limit sun exposure and avoid grapefruit and products containing grapefruit. 
        </SummarySection>
      </Anchor>

      <Stack
          pt={2}
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="center"
          spacing={{ xs: 2, md: 3 }}
          margin={0}
          mb={4}
        >
        <Callout
          className="tertiary"
          cta={
            <Link
              component={Button}
              className="flex"
              trackingId="resources"
              sx={{
                outline: 'none',
                color: 'black',

                '&:hover': {
                  outline: 'none',
                  backgroundColor: '#062059',
                  color: 'white',
                  svg: { fill: 'white' },
                },
              }}
              href="/patient-caregiver-resources"
            >
              Find support
            </Link>
          }
        >
          <Typography>
            <strong>Get resources you need</strong>
          </Typography>
        </Callout>

        <Callout
          cta={
            <Link
              component={Button}
              className="flex"
              trackingId="resources"
              sx={{
                outline: 'none',
                color: 'black',

                '&:hover': {
                  outline: 'none',
                  backgroundColor: '#062059',
                  color: 'white',
                  svg: { fill: 'white' },
                },
              }}
              href="/about-ros1-nsclc"
            >
              Learn more
            </Link>
          }
        >
          <Typography>
            <strong>About ROS1+ NSCLC</strong>
          </Typography>
        </Callout>
      </Stack>
    </StyledDosing>
  )
}
